import { PersistentSocket } from "./PersistentSocket";
import {
  StateBuilder,
  type Model as StateBuilderModel,
  type QueryDescriptor,
} from "./StateBuilder";

interface ModelFieldDescriptor {
  anchor: string;
  model: StateBuilderModel;
  /** Set for a `many=True` field (design D6): routes the field through
   * membership derivation instead of the single-anchor rebuild. */
  many?: boolean;
}

/**
 * Reactive surface exposed by every ContextChannel.
 *
 * `subscribe` and `getVersion` are bound to the channel instance so their
 * identity is stable across renders — required by `useSyncExternalStore`.
 * `getVersion` returns a monotonically increasing integer; React diffs it
 * with `Object.is` to decide whether to re-render.
 */
export interface ChannelRx {
  subscribe: (listener: () => void) => () => void;
  getVersion: () => number;
  /**
   * RPC entry point used by generated action wrappers. The makefrontend
   * code emitter produces `this.rx.callAction('methodName', [arg1, arg2])`
   * for every `@action` on the Django channel.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callAction: (action: string, params: any[]) => Promise<any>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Pending = { resolve: (v: any) => void; reject: (e: any) => void };

/**
 * Base class for generated TypeScript channel classes.
 *
 * Subclasses are emitted by `makefrontend` and bind a Django ContextChannel
 * to a typed React surface. State (e.g. `channel.counter`) is exposed
 * directly on the instance; the `rx` object carries the React subscription
 * plumbing.
 *
 * @template T - The type of the root/anchor state object.
 */
export abstract class ContextChannel<T = unknown> {
  declare protected readonly _state?: T;

  private _version = 0;
  private readonly _listeners = new Set<() => void>();
  private _socket?: PersistentSocket;
  private _actionSeq = 0;
  private readonly _pending = new Map<string, Pending>();
  private readonly _sendQueue: string[] = [];
  private readonly _stateBuilders = new Map<string, StateBuilder<unknown>>();

  /**
   * Map of rx.model field name → descriptor used to rebuild flat layers
   * into nested instances. Emitted by ``makefrontend`` for channels that
   * declare rx.model fields; empty otherwise.
   */
  protected _modelFields: Record<string, ModelFieldDescriptor> = {};

  /**
   * Subclasses generated by `makefrontend` set these to the websocket
   * base URL and Django URL pattern respectively. The base class joins
   * them and connects lazily on first React subscription.
   */
  protected endpoint: string = '';
  protected baseURL: string = '';

  constructor(url?: string) {
    if (url) this._connect(url);
  }

  readonly rx: ChannelRx = {
    subscribe: (listener: () => void) => {
      const wasUnsubscribed = this._listeners.size === 0;
      this._listeners.add(listener);
      if (wasUnsubscribed) {
        if (this._socket === undefined) {
          if (this.baseURL || this.endpoint) {
            this._connect(`${this.baseURL}${this.endpoint}`);
          }
        } else {
          // A remount after the last subscriber unmounted (design D5):
          // re-arm retrying and, if currently down, reconnect right away.
          this._socket.resume();
        }
      }
      return () => {
        this._listeners.delete(listener);
        if (this._listeners.size === 0) {
          // react-client "Persistent socket with backoff": unmounting the
          // last subscriber stops reconnection -- no further attempts are
          // scheduled while nobody is listening.
          this._socket?.stop();
        }
      };
    },
    getVersion: () => this._version,
    callAction: (action, params) => this._callAction(action, params),
  };

  private _connect(url: string): void {
    const socket = new PersistentSocket(url);
    this._socket = socket;
    socket.onOpen = () => {
      while (this._sendQueue.length) {
        socket.send(this._sendQueue.shift()!);
      }
    };
    socket.onMessage = (data: string) => {
      // Each frame is handled in its own macrotask so awaiters of
      // `callAction` can resolve and re-subscribe before the next
      // server-pushed message is processed (e.g. an `rx` update that
      // immediately follows an `ac` response).
      setTimeout(() => this._onMessage(data), 0);
    };
    socket.connect();
  }

  private _onMessage(raw: string): void {
    let msg: { t?: string; [k: string]: unknown };
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }
    switch (msg.t) {
      case "ready":
        this._notify();
        return;
      case "rx": {
        const field = msg.f as string;
        const op = msg.o as string | undefined;
        if (op !== undefined) {
          // List delta operation (ADR-0017): `v` is the op's operand, not a
          // full value. Applied positionally, in arrival order.
          this._applyListOp(field, op, msg.v);
          this._notify();
          return;
        }
        if ("v" in msg) {
          const descriptor = this._modelFields[field];
          if (descriptor !== undefined) {
            // `q`: the bind descriptor (ADR-0019 D1), present only on a
            // `many=True` field's snapshot anchor frame.
            const query = msg.q as QueryDescriptor | undefined;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this as any)[field] = this._rebuildModelField(field, descriptor, msg.v, query);
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this as any)[field] = msg.v;
          }
        }
        this._notify();
        return;
      }
      case "ac": {
        const id = msg.id as string;
        const pending = this._pending.get(id);
        if (!pending) return;
        this._pending.delete(id);
        const err = msg.e;
        if (Array.isArray(err)) {
          const e: Error & { code?: number } = new Error(
            String(err[1] ?? "action failed"),
          );
          e.code = Number(err[0]);
          pending.reject(e);
        } else {
          pending.resolve(msg.r);
        }
        return;
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected async _callAction(action: string, params: any[]): Promise<any> {
    if (!this._socket) {
      throw new Error("ContextChannel: no websocket connection");
    }
    const id = String(++this._actionSeq);
    const payload = JSON.stringify({ t: "ac", a: action, id, p: params });
    const promise = new Promise((resolve, reject) => {
      this._pending.set(id, { resolve, reject });
    });
    if (!this._socket.send(payload)) {
      // Disconnected (including mid-backoff): queued and flushed on the
      // next open, per the existing pre-open queue rule -- unchanged by
      // the persistent socket, since a reconnect is just another open.
      this._sendQueue.push(payload);
    }
    return promise;
  }

  /**
   * Apply a list delta operation (ADR-0017) to `field` in place, publishing
   * a new array identity so React re-renders. Ops are exclusive to list
   * fields (wire-protocol): if the field's current value isn't an array —
   * e.g. it's still `null` on an optional list, or the op arrived for a
   * non-list field entirely — the frame is discarded per the wire spec (D4).
   */
  private _applyListOp(field: string, op: string, operand: unknown): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const current = (this as any)[field];
    if (!Array.isArray(current)) {
      return;
    }
    const next = current.slice();
    switch (op) {
      case "i": {
        const [index, value] = operand as [number, unknown];
        next.splice(index, 0, value);
        break;
      }
      case "s": {
        const [index, value] = operand as [number, unknown];
        next[index] = value;
        break;
      }
      case "d": {
        const index = operand as number;
        next.splice(index, 1);
        break;
      }
      default:
        // Unknown op: forward-compatibility posture — ignore silently.
        return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any)[field] = next;
  }

  private _rebuildModelField(
    field: string,
    descriptor: ModelFieldDescriptor,
    value: unknown,
    query?: QueryDescriptor,
  ): unknown {
    if (value === null || value === undefined) {
      this._stateBuilders.delete(field);
      return null;
    }
    if (!Array.isArray(value)) {
      return value;
    }
    let builder = this._stateBuilders.get(field);
    if (builder === undefined) {
      builder = new StateBuilder(descriptor.model, descriptor.anchor, descriptor.many);
      this._stateBuilders.set(field, builder);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    builder.update(value as any, query);
    return builder.state;
  }

  /**
   * Mark the channel state as updated and notify React subscribers.
   */
  protected _notify(): void {
    this._version++;
    this._listeners.forEach((listener) => listener());
  }
}

export default ContextChannel;
